import {
  confirmSignIn,
  resetPassword,
  fetchAuthSession,
  signIn as AwsSignIn,
  confirmResetPassword,
  signOut as AwsSignOut,
} from 'aws-amplify/auth';
import type { SagaIterator } from 'redux-saga';
import { call, put, takeLatest, take, takeEvery, fork, delay } from 'redux-saga/effects';

import { navigateToForgotPasswordCheckMail, navigateToHomePage } from 'actions/navigateActions';
import * as userActions from 'actions/userActions';
import { USER_NOT_FOUND } from 'constants/ErrorConstants';

import { setAuthStep } from 'modules/auth/actions';
import * as authActions from 'modules/auth/actions';
import * as types from 'modules/auth/actionTypes';
import { LOGIN_STEPS } from 'modules/auth/constant';
import type {
  SignInRequestAction,
  SignOutRequestAction,
  SignOutEmergencyAction,
  ForgotPasswordRequestAction,
  ForgotPasswordSubmitRequestAction,
} from 'modules/auth/types';
import { unsubscribe } from 'modules/notifications/actions';
import { createNotification } from 'modules/toast-notifications/actions';
import { sendBroadcastMessage, closeBroadcastChannel, SUCCESS_LOGIN_MESSAGE } from 'utils/broadcastChannel';

import impersonateSaga from './impersonate';

/** The shape of what Amplify's auth calls actually throw — not a formal Amplify contract. */
type SignInError = { code?: string; message?: string };

type NotificationMessage = { id: string; defaultMessage: string };

const resolveSignInErrorMessage = (message?: string): NotificationMessage => {
  if (message?.includes('[FleetbackAuthenticationError:INACTIVE]')) {
    return {
      id: 'page.login.inactiveUser',
      defaultMessage:
        'Your user account is inactive in Fleetback. If you think this is an error, please contact your manager.',
    };
  }
  if (message?.includes('[FleetbackAttemptsReached:OTP]')) {
    return {
      id: 'page.login.mfa.error.otpMaxAttempts',
      defaultMessage: 'Maximum number of login attempts reached',
    };
  }
  if (message?.includes('Invalid session for the user.')) {
    return {
      id: 'page.login.expiredSession',
      defaultMessage: 'Your session has expired.',
    };
  }
  return {
    id: 'page.login.error',
    defaultMessage: 'The specified credentials are incorrect, please try again.',
  };
};

function* createSignInErrorNotification(error: SignInError) {
  const { code, message } = error;

  const notification =
    code === 'NetworkError'
      ? {
        type: 'error',
        message: {
          id: 'http.error.message',
          defaultMessage: 'Oops! Something went wrong.',
        },
      }
      : {
        type: 'simple-error',
        message: resolveSignInErrorMessage(message),
      };

  yield put(createNotification(notification));
}

function* resendVerificationCode() {
  try {
    const { nextStep } = yield call(confirmSignIn, {
      challengeResponse: '0',
      options: {
        clientMetadata: {
          resendCode: 'true',
        },
      },
    });
    yield put(authActions.resendVerifyCodeSuccess());
    return nextStep;
  } catch (error) {
    yield put(authActions.resendVerifyCodeFailure());
    yield call(createSignInErrorNotification, error as SignInError);
  }

  return null;
}

function* verifySignIn(code: string) {
  try {
    const { nextStep } = yield call(confirmSignIn, { challengeResponse: code });

    if (nextStep?.signInStep === 'DONE') {
      yield put(authActions.verifySignInSuccess());
      return nextStep;
    }

    yield put(authActions.verifySignInFailure(new Error('Wrong OTP')));
  } catch (e) {
    const error = e as Error;
    if (error.message === 'Challenge response cannot be empty') {
      yield put(authActions.verifySignInFailure(new Error('OTP empty')));
    } else {
      const finalError = error.message === 'Invalid session for the user.' ? error : new Error('[FleetbackAttemptsReached:OTP]');

      yield put(setAuthStep(LOGIN_STEPS.LOGIN));
      yield put(authActions.verifySignInFailure(finalError));

      throw finalError;
    }
  }

  return null;
}

function* signIn({ payload }: SignInRequestAction): SagaIterator {
  try {
    const { username, password } = payload;

    yield put(setAuthStep(LOGIN_STEPS.LOGIN, { username }));
    let { nextStep } = yield call(AwsSignIn, { username, password, options: { authFlowType: 'CUSTOM_WITH_SRP' } });

    if (nextStep.additionalInfo?.type === LOGIN_STEPS.MFA_EMAIL) {
      do {
        yield put(setAuthStep(LOGIN_STEPS.MFA_EMAIL, nextStep?.additionalInfo));
        const { type, payload: code } = yield take([types.VERIFY_SIGN_IN_REQUEST, types.RESEND_VERIFY_CODE_REQUEST]);

        if (type === types.RESEND_VERIFY_CODE_REQUEST) {
          nextStep = yield call(resendVerificationCode);
        } else {
          nextStep = yield call(verifySignIn, code);
        }
      } while (nextStep?.signInStep !== 'DONE');
    }
    const { tokens } = yield call(fetchAuthSession);
    yield put(authActions.signInSuccess(tokens));

    if (username) {
      sendBroadcastMessage(username, SUCCESS_LOGIN_MESSAGE);
      yield delay(100);
      closeBroadcastChannel(username);
    }
  } catch (error) {
    yield call(createSignInErrorNotification, error as SignInError);
    yield put(authActions.signInFailure(error as Error));
  }
}

function* signOut({ payload }: SignOutRequestAction | SignOutEmergencyAction) {
  try {
    if (payload?.token?.length) {
      yield put(unsubscribe(payload));
    }

    yield call(AwsSignOut);

    yield put(userActions.logoutUserClear());
    yield put(authActions.signOutSuccess());
  } catch (e) {
    yield put(authActions.signOutFailure(e as Error));
  }
}

export function* loadSession(action?: { meta?: Record<string, unknown> }, params?: { forceRefresh?: boolean }) {
  try {
    const { tokens } = yield call(fetchAuthSession, params);
    if (!tokens?.idToken) {
      yield put(authActions.loadSessionFailure(null, action?.meta));
      return;
    }
    yield put(authActions.loadSessionSuccess(tokens, action?.meta));
  } catch (e) {
    yield put(authActions.loadSessionFailure(e !== 'No current user' ? (e as Error) : null, action?.meta));
  }
}

function* forgotPassword({ payload: username }: ForgotPasswordRequestAction) {
  try {
    yield call(resetPassword, { username });
    yield put(setAuthStep(LOGIN_STEPS.LOGIN, { username }));
    yield put(authActions.forgotPasswordSuccess());
    yield put(
      createNotification({
        type: 'success',
        message: {
          id: 'authentication.forgotPassword.codeRequest.codeSent',
          defaultMessage:
            'An e-mail containing a verification code has been sent to the address you provided: {username}',
          values: { username },
        },
      })
    );
    yield call(navigateToForgotPasswordCheckMail);
  } catch (e) {
    yield put(authActions.forgotPasswordFailure(e as Error));
    const error = e as { code?: string };
    const message =
      error?.code === USER_NOT_FOUND
        ? {
          id: 'authentication.forgotPassword.error.unknownUser',
          defaultMessage: 'An unknown error has occurred while submitting your identification code request',
        }
        : {
          id: 'authentication.forgotPassword.error.unknown',
          defaultMessage: 'Incorrect email / username and or password.',
        };

    yield put(
      createNotification({
        type: 'error',
        message,
      })
    );
  }
}

function* forgotPasswordSubmit({ payload }: ForgotPasswordSubmitRequestAction) {
  try {
    yield call(confirmResetPassword, payload);
    yield put(authActions.forgotPasswordSubmitSuccess());
    yield put(
      createNotification({
        type: 'success',
        message: {
          id: 'authentication.forgotPassword.submitCode.success',
          defaultMessage: 'Your new password is activated.',
        },
      })
    );
    yield call(navigateToHomePage);
  } catch (e) {
    yield put(authActions.forgotPasswordSubmitFailure(e as Error));
    yield put(
      createNotification({
        type: 'error',
        message: {
          id: 'authentication.forgotPassword.submitCode.error',
          defaultMessage: 'The verification code is incorrect.',
        },
      })
    );
  }
}

export default function* authSagas() {
  yield takeLatest(types.SIGN_IN_REQUEST, signIn);
  yield takeLatest(types.LOAD_SESSION_REQUEST, loadSession);
  yield takeLatest(types.FORGOT_PASSWORD_REQUEST, forgotPassword);
  yield takeEvery([types.SIGN_OUT_REQUEST, types.SIGN_OUT_EMERGENCY], signOut);
  yield takeLatest(types.FORGOT_PASSWORD_SUBMIT_REQUEST, forgotPasswordSubmit);

  yield fork(impersonateSaga);
}
